import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { QuoteForm } from '@/components/widgets/quote-form'
import { EmergencyNote, QuoteSteps } from '@/components/landing/sections/quote-section'
import { FaqSection } from '@/components/landing/sections/faq-section'
import { getDiagnosisById } from '@/constants/diagnosis'
import { getFormService, getFormServiceBySlug } from '@/constants/quote-form'
import { reviewStats } from '@/constants/trust'

const readParam = (value: string | string[] | undefined): string =>
    typeof value === 'string' ? value : Array.isArray(value) ? value[0] ?? '' : ''

const RequestServicePage: FC = (): ReactNode => {
    const router = useRouter()

    const serviceParam = readParam(router.query.service)
    const diagnosisParam = readParam(router.query.diagnosis)
    const entry = readParam(router.query.entry) || 'request_page'

    const diagnosis = diagnosisParam ? getDiagnosisById(diagnosisParam) : undefined
    const formService = diagnosis ? getFormService(diagnosis.formService) : serviceParam ? getFormServiceBySlug(serviceParam) : undefined
    const initialAnswers = diagnosis ? { ...(diagnosis.answers ?? {}), diagnosis: diagnosis.id } : {}

    return (
        <Layout>
            <Seo
                title='Request service'
                description="Tell us what your garage door is doing and get a written price range by text, usually within 30 minutes. Covering eight Riverton metro towns."
            />

            <section id='request' className='relative overflow-hidden pb-24 pt-36 md:pb-32 md:pt-44'>
                <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-40 size-[42rem] rounded-full bg-ember/[0.07] blur-3xl' />

                <Container className='relative grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <div className='lg:sticky lg:top-32'>
                            <Enter delay={60}>
                                <Eyebrow>Start here</Eyebrow>
                            </Enter>

                            <HeroTitle
                                text="Your written price, usually within 30 minutes."
                                accent={['30', 'minutes.']}
                                className='mt-6 max-w-[13ch] text-h1 font-semibold text-ink'
                            />

                            <Enter delay={420} className='mt-7 max-w-md text-lead text-pretty text-slate'>
                                You only see the questions that matter for your job. A photo is optional, but it helps us load the right parts before we arrive.
                            </Enter>

                            {diagnosis && (
                                <Enter delay={480} className='mt-6'>
                                    <p className='inline-flex rounded-full bg-sand px-4 py-2 text-[14px] text-ink'>
                                        Based on your diagnosis: <span className='ml-1 font-medium'>{diagnosis.title}</span>
                                    </p>
                                </Enter>
                            )}

                            <Enter delay={540} className='mt-10'>
                                <p className='mb-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate'>After you send it</p>
                                <QuoteSteps />
                            </Enter>

                            <Enter delay={620}>
                                <EmergencyNote />

                                <p className='mt-6 font-mono text-[11.5px] uppercase tracking-[0.14em] text-slate'>
                                    Rated {reviewStats.rating} across {reviewStats.count} reviews
                                </p>
                            </Enter>
                        </div>
                    </div>

                    <Enter delay={300} className='lg:col-span-7'>
                        {router.isReady ? (
                            <QuoteForm
                                key={`${formService?.id ?? 'none'}-${diagnosis?.id ?? 'none'}`}
                                initialService={formService?.id ?? null}
                                initialAnswers={initialAnswers}
                                entry={entry}
                            />
                        ) : (
                            <div aria-hidden='true' className='min-h-[36rem] rounded-[32px] border border-ink/[0.08] bg-paper' />
                        )}
                    </Enter>
                </Container>
            </section>

            <FaqSection className='bg-sand/60 py-24 md:py-32' />
        </Layout>
    )
}

export default RequestServicePage
